import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  AnimatePresence,
} from 'framer-motion';
import logo from '../assets/autoboy_logo3.png';
import phone1 from '../assets/images/phone1.jpg';
import phone2 from '../assets/images/phone2.jpg';
import phone3 from '../assets/images/phone3.jpg';
import phone4 from '../assets/images/phone4.jpg';
import phone5 from '../assets/images/phone5.jpg';
import phone7 from '../assets/images/phone7.jpg';
import './HeroSection.css';

/* ── Data ─────────────────────────────────────────── */
const BG_IMAGES = [
  'https://images.unsplash.com/photo-1573739022854-abceaeb585dc?w=1920&h=1080&fit=crop&q=90',
  'https://images.unsplash.com/photo-1611411802484-3f47f53e30e3?w=1920&h=1080&fit=crop&q=90',
  'https://images.unsplash.com/photo-1643536768014-0756fa85ca4f?w=1920&h=1080&fit=crop&q=90',
  'https://images.unsplash.com/photo-1609081219090-a6d81d3085bf?w=1920&h=1080&fit=crop&q=90',
  'https://images.unsplash.com/photo-1672718985175-be33c65e17f8?w=1920&h=1080&fit=crop&q=90',
];

const PHONE_CYCLE = [phone1, phone5, phone7, phone3];

const STATS = [
  { value: '50K+',   label: 'Active Buyers'   },
  { value: '10K+',   label: 'Products'        },
  { value: '₦500M+', label: 'Transactions'    },
  { value: '99.2%',  label: 'Delivery Rate'   },
  { value: '4.9★',   label: 'Avg Rating'      },
  { value: '2K+',    label: 'Sellers'         },
];

const HEADLINE = 'FUTURE';

/* ── Framer variants ──────────────────────────────── */
const charVariants = {
  hidden: { opacity: 0, y: 100, rotateX: -80 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    rotateX: 0,
    transition: {
      delay: 0.55 + i * 0.06,
      duration: 0.65,
      ease: [0.215, 0.61, 0.355, 1.0],
    },
  }),
};

const fadeUp = (delay = 0) => ({
  hidden:  { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, delay, ease: [0.215,0.61,0.355,1] } },
});

const fadeRight = (delay = 0) => ({
  hidden:  { opacity: 0, x: 30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.65, delay, ease: [0.215,0.61,0.355,1] } },
});

/* ── Magnetic button wrapper ──────────────────────── */
const MagneticWrap = ({ children, strength = 0.38, radius = 90 }) => {
  const ref   = useRef(null);
  const mx    = useMotionValue(0);
  const my    = useMotionValue(0);
  const sx    = useSpring(mx, { stiffness: 220, damping: 18 });
  const sy    = useSpring(my, { stiffness: 220, damping: 18 });

  const onMove = useCallback((e) => {
    if (!ref.current) return;
    const r  = ref.current.getBoundingClientRect();
    const dx = e.clientX - (r.left + r.width  / 2);
    const dy = e.clientY - (r.top  + r.height / 2);
    if (Math.sqrt(dx * dx + dy * dy) < radius) {
      mx.set(dx * strength);
      my.set(dy * strength);
    }
  }, [mx, my, strength, radius]);

  const onLeave = useCallback(() => { mx.set(0); my.set(0); }, [mx, my]);

  return (
    <motion.div
      ref={ref}
      style={{ x: sx, y: sy, display: 'inline-block' }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      {children}
    </motion.div>
  );
};

/* ══════════════════════════════════════════════════
   HeroSection — 2026 Ultra-Modern
   ══════════════════════════════════════════════════ */
const HeroSection = () => {
  const navigate        = useNavigate();
  const sectionRef      = useRef(null);
  const [bgIndex, setBgIndex] = useState(0);

  /* Mouse-follow state for parallax */
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const mx   = useSpring(rawX, { stiffness: 45, damping: 18 });
  const my   = useSpring(rawY, { stiffness: 45, damping: 18 });

  /* Layer-specific parallax depths */
  const bgTX     = useTransform(mx, [-1, 1], [-18, 18]);
  const bgTY     = useTransform(my, [-1, 1], [-10, 10]);
  const frontX   = useTransform(mx, [-1, 1], [-28, 28]);
  const frontY   = useTransform(my, [-1, 1], [-18, 18]);
  const midX     = useTransform(mx, [-1, 1], [-14, 14]);
  const midY     = useTransform(my, [-1, 1], [-10, 10]);
  const backX    = useTransform(mx, [-1, 1], [-7, 7]);
  const backY    = useTransform(my, [-1, 1], [-5, 5]);
  const logoX    = useTransform(mx, [-1, 1], [-8, 8]);
  const logoY    = useTransform(my, [-1, 1], [-5, 5]);
  const tagX     = useTransform(mx, [-1, 1], [-18, 18]);
  const tagY     = useTransform(my, [-1, 1], [-12, 12]);
  const badgeX   = useTransform(mx, [-1, 1], [-10, 10]);
  const badgeY   = useTransform(my, [-1, 1], [-8, 8]);

  /* Background slideshow — cinematic 6s */
  useEffect(() => {
    const id = setInterval(() => setBgIndex(p => (p + 1) % BG_IMAGES.length), 6000);
    return () => clearInterval(id);
  }, []);

  /* Normalised mouse position (-1 to +1) */
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const handle = (e) => {
      const r = el.getBoundingClientRect();
      rawX.set((e.clientX - r.left)  / r.width  * 2 - 1);
      rawY.set((e.clientY - r.top)   / r.height * 2 - 1);
    };
    el.addEventListener('mousemove', handle);
    return () => el.removeEventListener('mousemove', handle);
  }, [rawX, rawY]);

  const handleShop = (e) => {
    e.preventDefault();
    navigate('/shop');
  };

  return (
    <section className="hs-section" ref={sectionRef}>

      {/* ── Cinematic Background ─────────────────── */}
      <div className="hs-bg-layer">
        <AnimatePresence mode="wait">
          <motion.div
            key={bgIndex}
            className="hs-bg-image"
            style={{
              backgroundImage: `url(${BG_IMAGES[bgIndex]})`,
              x: bgTX,
              y: bgTY,
            }}
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1,  scale: 1.02 }}
            exit={{    opacity: 0,  scale: 0.97 }}
            transition={{ duration: 1.6, ease: [0.43, 0.13, 0.23, 0.96] }}
          />
        </AnimatePresence>
        <div className="hs-bg-vignette" />
      </div>

      {/* ── Ambient orbs ─────────────────────────── */}
      <div className="hs-orb hs-orb-1" />
      <div className="hs-orb hs-orb-2" />
      <div className="hs-orb hs-orb-3" />

      {/* ══ MAIN GRID ════════════════════════════ */}
      <div className="hs-grid">

        {/* ── LEFT: Typography + CTAs ──────────── */}
        <div className="hs-left">

          {/* Brand logo above badge */}
          <motion.div
            className="hs-brand-icon"
            variants={fadeUp(0.05)}
            initial="hidden"
            animate="visible"
          >
            <img src={logo} alt="AutoboyExpress" className="hs-brand-icon-img" />
          </motion.div>

          {/* Live badge */}
          <motion.div
            className="hs-badge"
            variants={fadeUp(0.2)}
            initial="hidden"
            animate="visible"
          >
            <span className="hs-badge-dot" />
            <span>LIVE</span>
            <span className="hs-badge-divider">|</span>
            <span>50K+ Buyers · Nigeria&apos;s #1 Gadget Market</span>
          </motion.div>

          {/* Eyebrow line */}
          <motion.p
            className="hs-eyebrow"
            variants={fadeUp(0.35)}
            initial="hidden"
            animate="visible"
          >
            Welcome to the
          </motion.p>

          {/* ── Big-Type headline with per-char stagger */}
          <div className="hs-big-title" aria-label={HEADLINE}>
            {HEADLINE.split('').map((ch, i) => (
              <motion.span
                key={i}
                className="hs-char"
                custom={i}
                variants={charVariants}
                initial="hidden"
                animate="visible"
              >
                {ch}
              </motion.span>
            ))}
          </div>

          {/* Subtitle */}
          <motion.p
            className="hs-subtitle"
            variants={fadeUp(0.9)}
            initial="hidden"
            animate="visible"
          >
            of Premium Gadgets
          </motion.p>

          {/* Description */}
          <motion.p
            className="hs-description"
            variants={fadeUp(1.05)}
            initial="hidden"
            animate="visible"
          >
            Buy, sell, and swap the latest tech.
            <br />Secured by escrow. Delivered across Nigeria.
          </motion.p>

          {/* ── CTA Buttons (Magnetic) ────────── */}
          <motion.div
            className="hs-ctas"
            variants={fadeUp(1.15)}
            initial="hidden"
            animate="visible"
          >
            <MagneticWrap>
              <motion.a
                href="#"
                className="hs-btn hs-btn-primary"
                onClick={handleShop}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.96 }}
              >
                <span>Shop Now</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </motion.a>
            </MagneticWrap>

            <MagneticWrap>
              <motion.a
                href="/become-seller"
                className="hs-btn hs-btn-seller"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.96 }}
              >
                <span>Become a Seller</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              </motion.a>
            </MagneticWrap>

            <MagneticWrap>
              <motion.a
                href="/login"
                className="hs-btn hs-btn-ghost"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.96 }}
              >
                Sign Up
              </motion.a>
            </MagneticWrap>
          </motion.div>

          {/* ── Live Stats Row ─────────────────── */}
          <motion.div
            className="hs-stats-row"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.7 }}
          >
            {STATS.map((s) => (
              <div key={s.label} className="hs-stat">
                <span className="hs-stat-value">{s.value}</span>
                <span className="hs-stat-label">{s.label}</span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* ── RIGHT: Device cluster + Chips ────── */}
        <div className="hs-right">

  

          {/* ── Floating chips ─────────────────── */}

          {/* Product tag (bottom-left) */}

          <motion.div className="hs-escrow-badge" style={{ x: badgeX, y: badgeY }} variants={fadeRight(1.8)} initial="hidden" animate="visible">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <span>Escrow Protected</span>
          </motion.div>


        </div>
      </div>

      {/* ── Scroll indicator ─────────────────────── */}
      <motion.div
        className="hs-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4, duration: 0.6 }}
        aria-hidden="true"
      >
        <motion.div
          className="hs-scroll-dot"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
        />
      </motion.div>
    </section>
  );
};

export default HeroSection;
